import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

import { actions } from "venuescanner-js/constants/leads";

import inboxService from "venuescanner-js/react/components/Inbox/services/inboxService";
import formatNumber from "venuescanner-js/helpers/formatNumber";
import store from "venuescanner-js/services/inbox/models/store";

import SpaceThumbnail from "venuescanner-js/react/components/Inbox/components/Objects/SpaceThumbnail";
import BEM from "venuescanner-js/utils/BEM";

const LeadAccept = ({ lead, leadCurrency, bem }) => {
  const priceInputRef = useRef();
  const STAGES = {
    MORE_INFO: 'more-info',
    SELECT_SPACE: 'select-space',
    SET_PRICING: 'set-pricing',
    LOADING: 'loading',
    CONFIRMATION: 'confirmation',
    ERROR: 'error',
  };
  const [stage, setStage] = useState(STAGES.MORE_INFO);
  const [spaces, setSpaces] = useState(null);
  const [editingPrice, setEditingPrice] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(lead.lead.price || 0);
  const venue = store.venue[lead.venue._id];
  const priceInterval = 100;

  useEffect(() => {
    if (spaces === null) {
      venue
        .fetchSpaces()
        .then(setSpaces)
        .catch((err) => {
          console.error("LeadAccept: fetchedSpaces error - ", err);
        });
    }
    if (!selectedSpace && spaces?.length === 1) setSelectedSpace(spaces[0]);
  });

  useEffect(() => {
    selectedPrice === 0 && setSelectedPrice(lead.lead.price || 0);
  }, [selectedSpace]);

  useEffect(() => {
    if (editingPrice) priceInputRef.current.focus();
  }, [editingPrice]);

  const addToPrice = () => {
    if (editingPrice)
      priceInputRef.current.value = selectedPrice + priceInterval;
    setSelectedPrice(selectedPrice + priceInterval);
  };
  const subtractFromPrice = () => {
    const value = selectedPrice - priceInterval;
    if (editingPrice) value < 0 ? 0 : (priceInputRef.current.value = value);
    setSelectedPrice(value < 0 ? 0 : value);
  };
  const updatePrice = () => {
    const value = parseFloat(priceInputRef.current.value);
    setSelectedPrice(parseFloat(value < 0 ? 0 : value));
  };

  const spaceList = spaces?.length ? (
    spaces.map((space) => (
      <SpaceThumbnail
        key={space._subjects._value._id}
        space={space}
        selectedSpace={selectedSpace}
        setSelectedSpace={setSelectedSpace}
      />
    ))
  ) : (
    <span className="loading" />
  );

  const getEventTime = ({ format }) => {
    try {
      const start = dayjs(lead.admin_event.date_from).format(format);
      const end = dayjs(
        lead.admin_event.date_to ?? lead.admin_event.date_from
      ).format(format);

      return start === end ? start : `${start} - ${end}`;
    } catch (error) {
      return "-";
    }
  };

  const handleSubmit = () => {
    setStage(STAGES.LOADING);
    inboxService
      .leadInterested({
        _id: lead._id,
        price: selectedPrice,
        space: selectedSpace._id,
      })
      .then(() => {
        setStage(STAGES.CONFIRMATION);
      })
      .catch(() => {
        setStage(STAGES.ERROR);
      });
  };

  if (!leadCurrency) return null;

  return (
    <div className={bem.cls()}>
      {stage === STAGES.MORE_INFO && (
        <div className={bem.elem("wrapper").cls()}>
          <div className={bem.elem("wrapper-title").cls()}>
            1/3 - Confirm availability
          </div>
          <h3 className={bem.elem("title").cls()}>
            Interested in hosting this event?
          </h3>
          <p className={bem.elem("sub-title").cls()}>
            Click continue if your venue is available and you're interested in
            hosting this event &#127881;
          </p>
          <div className={bem.elem("info").cls()}>
            <div className={bem.elem("info-row").cls()}>
              <span className={bem.elem("info-row-title").cls()}>
                Event type:
              </span>
              <span className={bem.elem("info-row-value").cls()}>
                {lead.admin_event?.event_type || "-"}
              </span>
            </div>
            <div className={bem.elem("info-row").cls()}>
              <span className={bem.elem("info-row-title").cls()}>Guests:</span>
              <span className={bem.elem("info-row-value").cls()}>
                {lead.admin_event.capacity}
              </span>
            </div>
            <div className={bem.elem("info-row").cls()}>
              <span className={bem.elem("info-row-title").cls()}>Date(s):</span>
              <span className={bem.elem("info-row-value").cls()}>
                {getEventTime({ format: "DD/MM/YYYY" })}
              </span>
            </div>
            <div className={bem.elem("info-row").cls()}>
              <span className={bem.elem("info-row-title").cls()}>Time:</span>
              <span className={bem.elem("info-row-value").cls()}>
                {getEventTime({ format: "hh:mm A" })}
              </span>
            </div>
            <div className={bem.elem("info-row").cls()}>
              <span className={bem.elem("info-row-title").cls()}>Budget:</span>
              <span className={bem.elem("info-row-value").cls()}>
                {lead.admin_event.budget
                  ? `${leadCurrency.currency.symbol}${formatNumber(
                      lead.admin_event.budget
                    )}`
                  : "-"}
              </span>
            </div>
            {lead.lead.message && (
              <div className={bem.elem("info-row").mods("wrap").cls()}>
                <span
                  className={bem.elem("info-row-title").mods("flex-1").cls()}
                >
                  Message from the organiser:
                </span>
                <span className={bem.elem("info-row-value").mods("long").cls()}>
                  {lead.lead.message}
                </span>
              </div>
            )}
          </div>

          <div className={bem.elem("ctas").cls()}>
            <div className={bem.elem('ctas-item').cls()}>
              <Link
                to={`/inbox/leads/${lead._id}?status=${actions.IGNORE.search_param}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button className="button button--outline-light">
                  Not interested
                </button>
              </Link>
            </div>
            <div className={bem.elem('ctas-item').cls()}>
              <button
                className="button button--light"
                onClick={() => setStage(STAGES.SELECT_SPACE)}
              >
                Continue
              </button>
            </div>
          </div>
          <div className={bem.elem("next").cls()}>
            Next: Confirm space details
          </div>
          <Link to="/inbox/leads" onClick={(e) => e.stopPropagation()}>
            <span className={bem.elem("text-link").cls()}>Go back</span>
          </Link>
        </div>
      )}

      {stage === STAGES.SELECT_SPACE && (
        <div className={bem.elem("wrapper").cls()}>
          <div className={bem.elem("wrapper-title").cls()}>
            2/3 - Confirm availability
          </div>
          <h3 className={bem.elem("title").cls()}>Confirm space details</h3>
          {spaces?.length > 1 ? (
            <p>Select a space you have availability for and click continue.</p>
          ) : (
            <p>Click continue if this space has availability.</p>
          )}
          <div className={bem.elem("spaces").cls()}>{spaceList}</div>
          <div className={bem.elem("ctas").cls()}>
            <button
              className={`lead-accept__ctas-item button button--light ${
                selectedSpace ? "isValid" : "isInvalid"
              }`}
              onClick={() => setStage(STAGES.SET_PRICING)}
            >
              Continue
            </button>
          </div>
          <div className={bem.elem("next").cls()}>Next: Confirm pricing</div>
          <span
            className={bem.elem("text-link").cls()}
            onClick={() => setStage(STAGES.MORE_INFO)}
          >
            Go back
          </span>
        </div>
      )}

      {stage === STAGES.SET_PRICING && (
        <div
          className={bem.elem("wrapper").cls()}
          onClick={() => {
            setEditingPrice(false);
          }}
        >
          <div className={bem.elem("wrapper-title").cls()}>
            3/3 - Confirm availability
          </div>
          <h3 className={bem.elem("title").cls()}>Confirm Price</h3>
          <p className={bem.elem("copy").cls()}>
            Increase your likelihood of winning this event by confirming your
            price to host this event.
          </p>
          <div className={bem.elem("pricing").cls()}>
            {editingPrice && (
              <input
                className={bem.elem("pricing-input").cls("h2")}
                type="number"
                min="0"
                defaultValue={selectedPrice}
                onChange={() => updatePrice()}
                ref={priceInputRef}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {!editingPrice && (
              <div
                className={bem.elem("pricing-display").cls()}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingPrice(true);
                }}
              >
                <span className={bem.elem("pricing-h2").mods("ccy").cls()}>
                  {leadCurrency.currency.symbol}
                </span>
                <span className={bem.elem("pricing-h2").cls()}>
                  {selectedPrice === 0 ? "___" : formatNumber(selectedPrice)}
                </span>
              </div>
            )}
            <div className={bem.elem("pricing-buttons").cls()}>
              <span
                className={bem.elem("pricing-button").cls("h4")}
                onClick={(e) => {
                  e.stopPropagation();
                  addToPrice();
                }}
              >
                +
              </span>
              <span
                className={bem
                  .elem("pricing-button")
                  .mods(selectedPrice === 0 ? "isInactive" : "isActive")
                  .cls("h4")}
                onClick={(e) => {
                  e.stopPropagation();
                  subtractFromPrice();
                }}
              >
                -
              </span>
            </div>
          </div>
          <div className={bem.elem("ctas").cls()}>
            <button
              className={`lead-accept__ctas-item button button--light ${
                selectedPrice ? "isValid" : "isInvalid"
              }`}
              onClick={() => handleSubmit()}
            >
              Submit
            </button>
          </div>
          <span
            className={bem.elem("text-link").cls("p")}
            onClick={(e) => {
              e.stopPropagation();
              setStage(STAGES.SELECT_SPACE);
            }}
          >
            Go back
          </span>
        </div>
      )}

      {stage === STAGES.CONFIRMATION && (
        <div className={bem.elem("wrapper").cls()}>
          <span className="h2">&#127881;</span>
          <h3 className={bem.elem("title").cls()}>
            The organiser has
            <br />
            your venue’s details
          </h3>
          <p className={bem.elem("pricing-copy").cls()}>
            We'll notify you if they're interested in proceeding by sending you
            an enquiry.
          </p>
          <div className={bem.elem("ctas").cls()}>
            <div className={bem.elem("ctas-item").cls()}>
              <Link to="/inbox/leads" onClick={(e) => e.stopPropagation()}>
                <button
                  className="button button--light"
                >
                  Continue
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {stage === STAGES.ERROR && (
        <div className={bem.elem("wrapper").cls()}>
          <span className="h2">&#9995;</span>
          <h3 className={bem.elem("title").cls()}>Woops...</h3>
          <p className={bem.elem("pricing-copy").cls()}>
            Something seems to have gone wrong, try again or contact one of our
            representatives via intercom.
          </p>
          <div className={bem.elem("ctas").cls()}>
            <div className={bem.elem("ctas-item").cls()}>
              <Link to="/inbox/leads" onClick={(e) => e.stopPropagation()}>
                <button
                  className="button button--light"
                >
                  Continue
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {stage === STAGES.LOADING && (
        <div className={bem.elem("wrapper").cls()}>
          <span className="loading" />
        </div>
      )}
    </div>
  );
};

LeadAccept.defaultProps = {
  bem: new BEM("lead-accept"),
};

export default LeadAccept;
